// frontend/src/pages/employee/ActivityLog.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { AuthContext } from '../../components/AuthContext';

const requirementOptions = [
  { value: 'Artificial Stone', label: 'Artificial Stone' },
  { value: 'Door', label: 'Door' },
  { value: 'Floor Grass', label: 'Floor Grass' },
  { value: 'Fountain', label: 'Fountain' },
  { value: 'Gazebo', label: 'Gazebo' },
  { value: 'Granite', label: 'Granite' },
  { value: 'GRC Zali', label: 'GRC Zali' },
  { value: 'Kitchen Appliances', label: 'Kitchen Appliances' },
  { value: 'Kitchen', label: 'Kitchen' },
  { value: 'Lighting Products', label: 'Lighting Products' },
  { value: 'Marble', label: 'Marble' },
  { value: 'Natural Stone', label: 'Natural Stone' },
  { value: 'Others', label: 'Others' },
  { value: 'Pavers', label: 'Pavers' },
  { value: 'Roofing Products', label: 'Roofing Products' },
  { value: 'Sanitary ware', label: 'Sanitary ware' },
  { value: 'Tiles', label: 'Tiles' },
  { value: 'Window', label: 'Window' },
];

const ActivityLogManagement = () => {
  const { user } = useContext(AuthContext);
  const [logs, setLogs] = useState([]);
  const [stations, setStations] = useState([]);

  // Our form fields (now includes 'station' as ObjectId, 'referenceName', 'date', and 'requirements' as an array)
  const [form, setForm] = useState({
    project: '',
    contactPerson: '',
    contactNumber: '',
    station: '',             // Will store station _id here
    referenceName: '',
    requirements: [],        // Array of strings
    remarks: '',
    date: '',                // The user-chosen date
    createdBy: user?.user?._id,
  });

  // Fetch stations and logs on mount
  useEffect(() => {
    const fetchStations = async () => {
      try {
        // You must have a Station model & route that returns e.g. [{_id, name}, ...]
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/stations`);
        setStations(response.data);
      } catch (error) {
        Swal.fire('Error', 'Failed to fetch stations', 'error');
      }
    };

    const fetchLogs = async () => {
      try {
        // GET logs for the current user
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/activity-logs/${user.user._id}`);
        setLogs(response.data);
      } catch (error) {
        Swal.fire('Error', 'Failed to fetch activity logs', 'error');
      }
    };

    if (user) {
      fetchStations();
      fetchLogs();
    }
  }, [user]);

  // Reusable function to refresh logs
  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/activity-logs/${user.user._id}`);
      setLogs(response.data);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch activity logs', 'error');
    }
  };

  // Handle changes for normal text inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  // Validate form fields
  const validateForm = () => {
    const {
      project,
      contactPerson,
      contactNumber,
      station,
      referenceName,
      requirements,
      remarks,
      date,
    } = form;

    // Check empty fields
    if (
      !project ||
      !contactPerson ||
      !contactNumber ||
      !station ||
      !referenceName ||
      !requirements.length ||
      !remarks ||
      !date
    ) {
      Swal.fire('Warning', 'All fields are required', 'warning');
      return false;
    }

    // Contact number must be 10-digit
    if (!/^\d{10}$/.test(contactNumber)) {
      Swal.fire('Error', 'Contact number should be a valid 10-digit number', 'error');
      return false;
    }

    return true;
  };

  // Reset form fields
  const resetForm = () => {
    setForm({
      project: '',
      contactPerson: '',
      contactNumber: '',
      station: '',
      referenceName: '',
      requirements: [],
      remarks: '',
      date: '',
      createdBy: user?.user?._id,
    });
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      // Post to the server
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/activity-logs`, form);
      Swal.fire('Success', 'Activity log created successfully', 'success');
      fetchLogs();
      resetForm();
    } catch (error) {
      Swal.fire('Error', 'Failed to create activity log', 'error');
    }
  };

  // For station selection (single select)
  const handleStationChange = (selected) => {
    // Store the station's _id
    setForm({ ...form, station: selected ? selected._id : '' });
  };

  // For requirements selection (multi select)
  const handleRequirementsChange = (selectedOptions) => {
    // selectedOptions => array of {value, label}
    const newReqs = selectedOptions.map((opt) => opt.value);
    setForm({ ...form, requirements: newReqs });
  };

  return (
    <div className="p-6 bg-white shadow rounded-lg">
      <h2 className="text-3xl font-bold text-gray-800 mb-4">Activity Log Management</h2>

      {/* FORM */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Project/Activity Name */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Project / Activity Name</label>
            <input
              type="text"
              name="project"
              value={form.project}
              onChange={handleInputChange}
              className="border p-2 rounded text-sm"
              placeholder="Project or Activity"
            />
          </div>

          {/* Contact Person */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Contact Person</label>
            <input
              type="text"
              name="contactPerson"
              value={form.contactPerson}
              onChange={handleInputChange}
              className="border p-2 rounded text-sm"
              placeholder="Contact Person"
            />
          </div>

          {/* Contact Number */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Contact Number</label>
            <input
              type="tel"
              name="contactNumber"
              value={form.contactNumber}
              onChange={handleInputChange}
              maxLength={10}
              className="border p-2 rounded text-sm"
              placeholder="10-digit number"
            />
          </div>
        </div>

        {/* Next row: Station, Reference, Date */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Station */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Station</label>
            <Select
              options={stations}          // e.g. [{ _id, name }, ...]
              getOptionLabel={(opt) => opt.name}
              getOptionValue={(opt) => opt._id}
              onChange={handleStationChange}
              className="text-sm"
              placeholder="Select Station..."
              // Show current selected station from form
              value={stations.find((s) => s._id === form.station) || null}
            />
          </div>

          {/* Reference Name */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Reference Name</label>
            <input
              type="text"
              name="referenceName"
              value={form.referenceName}
              onChange={handleInputChange}
              className="border p-2 rounded text-sm"
              placeholder="Reference"
            />
          </div>

          {/* Date */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              name="date"
              value={form.date}
              onChange={handleInputChange}
              className="border p-2 rounded text-sm"
            />
          </div>
        </div>

        {/* Requirements (multi select) + Remarks */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Requirements */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Requirements</label>
            <Select
              options={requirementOptions}
              isMulti
              onChange={handleRequirementsChange}
              className="text-sm"
              placeholder="Select Requirements..."
              // Show currently selected values
              value={requirementOptions.filter((opt) => form.requirements.includes(opt.value))}
            />
          </div>

          {/* Remarks */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <textarea
              name="remarks"
              value={form.remarks}
              onChange={handleInputChange}
              rows={3}
              className="border p-2 rounded text-sm"
              placeholder="Enter remarks"
            />
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="mt-2 bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
        >
          Add Activity Log
        </button>
      </form>

      {/* ACTIVITY LOGS */}
      <div className="mt-8">
        <h3 className="text-2xl font-semibold text-gray-700 mb-3">Activity Logs</h3>
        {logs.length === 0 && <p>No activity logs found.</p>}

        {/* Responsive grid for logs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {logs.map((log, index) => {
            // Convert date to a nicer format
            const logDate = log.date ? new Date(log.date).toLocaleDateString() : 'N/A';
            const stationName = log.station?.name || 'N/A';
            return (
              <div key={index} className="bg-white rounded-lg shadow p-4 hover:bg-gray-50">
                <p>
                  <span className="font-semibold">ID:</span> {log.id}
                </p>
                <p>
                  <span className="font-semibold">Project:</span> {log.project}
                </p>
                <p>
                  <span className="font-semibold">Contact Person:</span> {log.contactPerson}
                </p>
                <p>
                  <span className="font-semibold">Contact Number:</span> {log.contactNumber}
                </p>
                <p>
                  <span className="font-semibold">Station:</span> {stationName}
                </p>
                <p>
                  <span className="font-semibold">Reference Name:</span> {log.referenceName}
                </p>
                <p>
                  <span className="font-semibold">Date:</span> {logDate}
                </p>
                <p>
                  <span className="font-semibold">Requirements:</span>{' '}
                  {log.requirements?.join(', ')}
                </p>
                <p>
                  <span className="font-semibold">Remarks:</span> {log.remarks}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ActivityLogManagement;